import React, { useState, useMemo, useRef, useEffect } from 'react';
import Stepper from '@/v2/components/Stepper';
import CouponPaymentStep from './Steps/CouponPaymentStep';
import BuyerInfoStep from './Steps/BuyerInfoStep';
import {
  useCartState,
  useCartDispatch,
} from '../../../components/context/CartContext/CartContext';
import useFetch from 'use-http';
import { useCartData } from '../CartPage/context/CartDataContext';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  buildCouponExponeaPayload,
  buildCouponTrackingData,
  persistBookingTrackingData,
  persistExponeaBookingPayload,
  pushBeginCheckoutTrackingData,
  pushExponeaBookingItemEvent,
  pushExponeaBookingResultEvent,
} from '@/v2/utils/bookingTracking';

const CouponCartPage = () => {
  const { coupon, loading, setLoading } = useCartData();
  const dispatch = useCartDispatch();
  const buyerFormRef = useRef();
  const { request: registrationRequest, response: registrationResponse } =
    useFetch('/Registrations/Coupons');
  const [stepIndex, setStepIndex] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const { step, orderId } = useParams();
  const [quantity, setQuantity] = useState(1);
  const history = useHistory();
  const stepTitles = useMemo(() => ['Mokėtojo duomenys', 'Apmokėjimas'], []);
  const quantityRef = useRef(1);
  const cartState = useCartState();
  const nextStep = async () => {
    if (stepIndex === 0) {
      const valid = await buyerFormRef.current.trigger();
      if (!valid) return;

      const formData = buyerFormRef.current.getValues();
      setLoading(true);
      await showPaymentOptions(formData);
    }

    // setStepIndex((i) => Math.min(i + 1, steps.length));
  };

  useEffect(() => {
    if (coupon == null && step != 1) {
      if (cartState.registrationId) {
        history.push('/payment/status/cancel');
      } else {
        history.push('/dovanu-kuponai');
      }
    }
  }, [coupon, history, step]);

  const handleQuantityChange = (newQ) => {
    setQuantity(newQ); // updates UI on re-render
    quantityRef.current = newQ; // always up-to-date, sync immediately
  };

  useEffect(() => {
    if (!coupon) return;

    pushBeginCheckoutTrackingData(
      buildCouponTrackingData({
        coupon,
        quantity: quantityRef.current,
      }),
    );
  }, [coupon]);

  const steps = useMemo(
    () => [
      {
        title: stepTitles[0],
        content: (
          <BuyerInfoStep
            ref={buyerFormRef}
            coupon={coupon}
            goNext={nextStep}
            onQuantityChange={(s) => {
              handleQuantityChange(s);
            }}
          />
        ),
      },
      {
        title: stepTitles[1],
        content: <CouponPaymentStep orderId={orderId} />,
      },
    ],
    [stepTitles, coupon],
  );

  useEffect(() => {
    if (step) {
      dispatch({ type: 'RESET_REGISTRATION_ID' });
      setStepIndex(step);
    }
  }, [step]);

  const isCompleted = stepIndex === steps.length;

  const prevStep = () => setStepIndex((i) => Math.max(i - 1, 0));

  async function showPaymentOptions(formData) {
    setFormSubmitting(true);
    const payload = {
      patientEmail: formData.payerEmail,
      patientName: formData.patientFirstName || formData.payerFirstName,
      patientSurname: formData.patientLastName || formData.payerLastName,
      patientPhoneNumber: formData.payerPhone,
      patientPersonalCode: formData.payerPersonalCode || null,
      amount: coupon.price,
      couponId: coupon.couponId,
      couponPriceId: coupon.priceId,
      count: quantityRef.current, // adjust if you have quantity selector
      frontUrls: {
        cancelFrontURL: '/payment/status/cancel',
        acceptFrontUrl: '/dovanu-kuponai/cart/1',
        errorFrontUrl: '/payment/status/cancel',
      },
    };

    // Optional: remove extra fields if needed
    delete payload.agreement2;
    delete payload.agreement3;

    try {
      const responseData = await registrationRequest.post('', payload);
      const couponOrderId = responseData?.couponOrderId;
      const trackingData = buildCouponTrackingData({
        coupon,
        orderId: couponOrderId,
        quantity: payload.count,
        value: payload.amount * payload.count,
      });
      const exponeaPayload = buildCouponExponeaPayload({
        coupon,
        formData,
        quantity: payload.count,
        orderId: couponOrderId,
      });

      if (registrationResponse.status >= 500) {
        pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
        // setErrorModalVisible(true);
      } else if (registrationResponse.status >= 400) {
        const { errors } = responseData;
        pushExponeaBookingResultEvent(exponeaPayload, 'Failed');
        // handle form errors here if needed
      } else {
        persistBookingTrackingData(trackingData);
        persistExponeaBookingPayload(exponeaPayload);
        pushExponeaBookingItemEvent(exponeaPayload);

        dispatch({
          type: 'SET_REGISTRATION_ID',
          registrationId: couponOrderId,
          orderType: 'coupon',
        });

        const payseraUrl = responseData.paymentUrl;

        window.location.href = payseraUrl;
      }
    } finally {
      setFormSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-[1440px] mx-auto h-full p-4 font-primary">
      <div className="mb-4">
        <Stepper
          steps={stepTitles}
          currentStep={isCompleted ? steps.length + 1 : stepIndex + 1}
        />
      </div>

      <div className="pb-4 min-h-40">
        {isCompleted ? (
          <div className="p-6 border rounded bg-white">
            <h3 className="text-lg font-semibold mb-2">Viskas paruošta!</h3>
            <p className="text-gray-600">
              Užsakymas pateiktas. Visi žingsniai užbaigti.
            </p>
          </div>
        ) : (
          steps[stepIndex]?.content
        )}
      </div>
    </div>
  );
};

export default CouponCartPage;
